import { OAuthError, OktaAuth } from '@okta/okta-auth-js';
import * as Sentry from '@sentry/react';

import { DISABLE_PARTNER_AUTO_LOGIN } from '@constants/storageKeys';
import { execute as executeGql } from '@utils/executeGql';

import { EventTypes, emitter } from '../event';
import { AuthClient } from '../lib/base';
import { ClientType, type ProfileResult, type QueryResult } from '../types';
import { getBaseUrl } from '../utils';

interface ClientOptions {
  clientId: string;
  issuer: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  storageKey: string;
  clientType: ClientType;
}

class OktaClient extends AuthClient<ClientOptions> {
  private client: OktaAuth;
  private clientType: ClientType;
  private handleStorageChange: (event: StorageEvent) => void;

  constructor(opts: ClientOptions) {
    super(opts);
    this.client = {} as OktaAuth;
    this.clientType = opts.clientType;
    this.handleStorageChange = this.onStorageChange.bind(this);
  }

  get type() {
    return this.clientType;
  }

  get isAuthenticated() {
    return this.authenticated;
  }

  private async onStorageChange(event: StorageEvent) {
    const { storageKey } = this.clientOptions;

    // If a sibling app updates the shared Okta token storage key in localStorage,
    // reload tokens and re-evaluate auth so this tab picks up the new login state.
    if (event.key === storageKey && event.newValue !== event.oldValue) {
      const tokens = await this.client.tokenManager.getTokens();
      this.client.tokenManager.setTokens(tokens);
      const isAuthenticated = await this.client.isAuthenticated();
      if (isAuthenticated) {
        this.authenticated = true;
        emitter.emit(EventTypes.ActiveClient, this.clientType);
      }
    }
  }

  private async start() {
    await this.client.isAuthenticated({ onExpiredToken: 'renew' });
    this.client.start();
    emitter.emit(EventTypes.ClientInitialized, this.clientType);
    this.started = true;

    this.client.authStateManager.subscribe(({ isAuthenticated }) => {
      // check auth state, e.g. if it transitions from authenticated to
      // non-authenticated, handle somehow. Maybe killing the basket session.
      // TBD
      if (isAuthenticated) {
        emitter.emit(EventTypes.ActiveClient, this.clientType);
      }

      this.authenticated = isAuthenticated ?? false;
    });

    window.addEventListener('storage', this.handleStorageChange);
  }

  init() {
    const {
      clientId, //
      issuer,
      redirectUri,
      postLogoutRedirectUri,
      storageKey,
    } = this.clientOptions;

    const oktaClient = new OktaAuth({
      clientId,
      issuer,
      redirectUri,
      postLogoutRedirectUri,
      scopes: ['offline_access', 'openid'],
      tokenManager: {
        autoRenew: true,
        expireEarlySeconds: 120,
        secure: true,
        storage: 'localStorage',
        storageKey,
      },
      pkce: true,
    });

    this.client = oktaClient;
    if (!this.started) {
      this.start();
    }
  }

  login(originalUri = location.href) {
    if (!this.started) {
      throw new Error('Client is not initialized');
    }

    this.client?.signInWithRedirect({ originalUri });
  }

  logout(redirectUrl = getBaseUrl()) {
    window.removeEventListener('storage', this.handleStorageChange);
    window.sessionStorage.setItem(DISABLE_PARTNER_AUTO_LOGIN, 'true');
    this.client.signOut({ postLogoutRedirectUri: redirectUrl });
  }

  async getProfile() {
    const query = `
      query ProfileInformation {
        profileInformation {
          ... on PartnerProfileInformation {
            isCorporate
          }
        }
      }
    `;

    const { data } = await executeGql<QueryResult>(
      query,
      undefined,
      this.client.getAccessToken()
    );

    const { profileInformation } = data;
    return profileInformation as ProfileResult;
  }

  async handleRedirect() {
    const originalUri: string =
      this.client.getOriginalUri(this.client.options.state) || getBaseUrl();

    if (this.client?.token.isLoginRedirect()) {
      try {
        const { tokens } = await this.client.token.parseFromUrl();
        this.client.tokenManager.setTokens(tokens);
        window.sessionStorage.removeItem(DISABLE_PARTNER_AUTO_LOGIN);
        const profile = await this.getProfile();

        // Non-corporate partners land on /partner by default, unless the
        // login was started with an explicit return URL.
        if (
          !profile.isCorporate &&
          this.clientType === ClientType.OktaExternal &&
          originalUri === getBaseUrl()
        ) {
          return window.location.replace(`${getBaseUrl()}/partner`);
        }

        return window.location.replace(originalUri);
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            component: 'PartnerCallback',
            location: 'redirectHandler',
          },
        });
        if (error instanceof OAuthError) {
          const message = error.message;
          window.sessionStorage.setItem(DISABLE_PARTNER_AUTO_LOGIN, 'true');
          emitter.emit(EventTypes.AuthError, message);
        }
      }
    }
  }

  async getOrRenewToken() {
    return this.client.getOrRenewAccessToken();
  }

  get token() {
    if (!this.client.getAccessToken) return null;
    return this.client?.getAccessToken() ?? null;
  }
}

export { OktaClient };
